/**
 * Dummy analytics payloads for Chart.js–based widgets (replace with API data later).
 */

/** KPI tiles keyed for persona-specific dashboards (see `getHeroMetricsForKeys`). */
export const heroMetricLibrary = {
  response: {
    key: "response",
    label: "Avg. mission response time",
    value: "4.2h",
    sub: "creation → departure",
    accent: "info",
    icon: "schedule",
  },
  decline: {
    key: "decline",
    label: "Decline / unfilled rate",
    value: "8.4%",
    sub: "last 90 days",
    accent: "warning",
    icon: "trending_down",
  },
  lives: {
    key: "lives",
    label: "Lives impacted (YTD)",
    value: "1,284",
    sub: "from completed missions",
    accent: "success",
    icon: "favorite",
  },
  churn: {
    key: "churn",
    label: "Account churn",
    value: "2.1%",
    sub: "inactive 90+ days",
    accent: "error",
    icon: "person_off",
  },
  conversion: {
    key: "conversion",
    label: "Signup → mission flown",
    value: "28%",
    sub: "sample cohort",
    accent: "primary",
    icon: "filter_alt",
  },
  repeat: {
    key: "repeat",
    label: "Repeat pilots",
    value: "156",
    sub: "multi-mission (rolling year)",
    accent: "info",
    icon: "flight",
  },
  missions: {
    key: "missions",
    label: "Missions supported",
    value: "24",
    sub: "at your airport (YTD)",
    accent: "primary",
    icon: "flight_takeoff",
  },
  categoriesSummary: {
    key: "categoriesSummary",
    label: "Active mission types",
    value: "6",
    sub: "medical, animal, vet…",
    accent: "success",
    icon: "category",
  },
  pilotScore: {
    key: "pilotScore",
    label: "Avg. pilot reliability",
    value: "88",
    sub: "network composite (dummy)",
    accent: "info",
    icon: "verified",
  },
  nonprofitScore: {
    key: "nonprofitScore",
    label: "Nonprofit coordination",
    value: "84",
    sub: "responsiveness index (dummy)",
    accent: "success",
    icon: "volunteer_activism",
  },
  missionsFlown: {
    key: "missionsFlown",
    label: "Your missions flown",
    value: "42",
    sub: "last 12 months",
    accent: "primary",
    icon: "airlines",
  },
};

/**
 * @param {string[]} keys - keys from `heroMetricLibrary`
 * @returns {Array<typeof heroMetricLibrary[string]>}
 */
export function getHeroMetricsForKeys(keys) {
  return (keys || [])
    .map((k) => heroMetricLibrary[k])
    .filter(Boolean);
}

/** @deprecated use `getHeroMetricsForKeys` with persona config */
export const heroMetrics = getHeroMetricsForKeys(["response", "decline", "lives", "churn"]);

/** Missions & lives over time */
export const impactOverTimeChart = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
  datasets: [
    {
      label: "Missions flown",
      color: "info",
      data: [42, 38, 55, 48, 62, 58, 71, 66, 74, 69, 81, 77],
    },
    {
      label: "Lives impacted",
      color: "success",
      data: [118, 102, 156, 134, 178, 165, 201, 188, 210, 195, 228, 214],
    },
  ],
};

/** Mission category tags — horizontal bar */
export const missionCategoryChart = {
  labels: [
    "Medical transport",
    "Animal rescue",
    "Veteran support",
    "Conservation",
    "Disaster relief",
    "Other",
  ],
  datasets: [
    {
      label: "Missions",
      data: [148, 96, 72, 54, 38, 22],
      color: "primary",
    },
  ],
};

/** Doughnut: filled vs declined/unfilled */
export const declineDoughnut = {
  labels: ["Filled & flown", "Declined", "Unfilled / expired"],
  datasets: {
    label: "missions",
    data: [78, 12, 10],
    backgroundColors: ["success", "warning", "error"],
  },
};

/** Pilot reliability — radar */
export const pilotReliabilityRadar = {
  labels: ["Show rate", "On-time dep.", "Communication", "Mission complete", "Satisfaction"],
  datasets: [
    {
      label: "Pilot pool",
      data: [88, 82, 91, 86, 89],
      color: "info",
      borderColor: "#17c1e8",
      borderWidth: 2,
    },
  ],
};

/** Nonprofit reliability — radar */
export const nonprofitReliabilityRadar = {
  labels: ["Response time", "Request accuracy", "Coordination", "Post-mission data", "Satisfaction"],
  datasets: [
    {
      label: "Nonprofits",
      data: [84, 79, 87, 81, 85],
      color: "success",
      borderColor: "#82d616",
      borderWidth: 2,
    },
  ],
};

/** User conversion funnel (percent of cohort) */
export const conversionFunnel = [
  { label: "Signed up", value: 100 },
  { label: "Profile complete", value: 76 },
  { label: "Resource request", value: 52 },
  { label: "Mission matched", value: 34 },
  { label: "Mission flown", value: 28 },
];

/** Churn trend — single line */
export const churnTrendChart = {
  labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
  datasets: [
    {
      label: "Churn %",
      color: "error",
      data: [2.8, 2.6, 2.4, 2.5, 2.2, 2.1],
    },
  ],
};

/** Repeat pilots — headline + sparkline */
export const repeatPilotChart = {
  labels: ["Q1", "Q2", "Q3", "Q4"],
  datasets: [
    {
      label: "Repeat pilots",
      color: "primary",
      data: [112, 128, 141, 156],
    },
  ],
};

/** Map: approximate US coords for dummy heat */
export const missionHeatPoints = [
  { pos: [32.8, -97.0], intensity: 0.9, label: "Dallas area" },
  { pos: [34.05, -118.25], intensity: 0.85, label: "Los Angeles" },
  { pos: [40.7, -74.0], intensity: 0.8, label: "NYC" },
  { pos: [41.9, -87.6], intensity: 0.65, label: "Chicago" },
  { pos: [33.45, -112.07], intensity: 0.55, label: "Phoenix" },
  { pos: [47.6, -122.3], intensity: 0.7, label: "Seattle" },
  { pos: [25.8, -80.2], intensity: 0.6, label: "Miami" },
  { pos: [39.95, -75.16], intensity: 0.58, label: "Philadelphia" },
];

/** US state codes → relative coverage 0–1 per org filter (dummy) */
export const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

/** Keys match filter tabs; values are 50-length arrays 0–1 */
export const operatingStateCoverageByFilter = {
  all: US_STATE_CODES.map((_, i) => 0.15 + ((i * 17) % 80) / 100),
  animal: US_STATE_CODES.map((_, i) => 0.1 + ((i * 13) % 75) / 100),
  medical: US_STATE_CODES.map((_, i) => 0.2 + ((i * 11) % 70) / 100),
  veteran: US_STATE_CODES.map((_, i) => 0.12 + ((i * 19) % 65) / 100),
  conservation: US_STATE_CODES.map((_, i) => 0.08 + ((i * 23) % 60) / 100),
  other: US_STATE_CODES.map((_, i) => 0.05 + ((i * 29) % 55) / 100),
};

/** Airport missions table (dummy) */
export const airportMissionRows = [
  { id: "M-24089", type: "Medical", status: "Arrived", origin: "KPAO", dest: "KRNO", date: "2026-03-28", lives: 2 },
  { id: "M-24076", type: "Animal", status: "Departed", origin: "KRNO", dest: "KSMF", date: "2026-03-22", lives: 1 },
  { id: "M-24051", type: "Veteran", status: "Arrived", origin: "KSFO", dest: "KPAO", date: "2026-03-18", lives: 1 },
  { id: "M-24033", type: "Medical", status: "Scheduled", origin: "KPAO", dest: "KLAX", date: "2026-03-12", lives: 3 },
  { id: "M-23998", type: "Other", status: "Arrived", origin: "KRNO", dest: "KPAO", date: "2026-03-02", lives: 2 },
];

/** Organizations supported this month */
export const orgsSupportedThisMonth = [
  { name: "Wings of Hope", type: "Medical transportation", missions: 4 },
  { name: "Pilots N Paws — West", type: "Animal rescue", missions: 3 },
  { name: "Veteran Airlift Command", type: "Veteran support", missions: 2 },
  { name: "Conservation Flight Network", type: "Conservation", missions: 1 },
];
